import React from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const team = [
  {
    name: 'Cesar Valdez',
    title: 'Chief Financial Officer',
    initials: 'CV',
    color: '#1B2A6B',
    bio: 'Cesar brings a decade of discipline and strategic thinking forged through U.S. Army service, where he developed the financial acumen and operational mindset that now drives Pace Plan\'s fiscal strategy and growth planning. As CFO, he oversees budgeting, pricing models, client contracts, and the financial framework that keeps every project profitable and transparent. His experience navigating complex, high-stakes environments in the military translates directly to managing the fast-moving demands of commercial property services in one of the nation\'s largest markets.',
    tags: ['U.S. Army Veteran', 'Financial Strategy', 'Contract Management', 'Operations'],
  },
  {
    name: 'Beder Edlibi',
    title: 'Chief Operating Officer',
    initials: 'BE',
    color: '#E83A2E',
    bio: 'Beder holds a degree in Marketing from the University of Houston and serves as the operational and growth engine behind Pace Plan. As COO, he oversees day-to-day operations, vendor relationships, client acquisition, and the systems that keep every job on schedule. His marketing background gives him a sharp eye for positioning and communication — ensuring that Pace Plan not only delivers exceptional work on-site but tells that story clearly to the commercial property managers, asset owners, and portfolio clients the company targets. Beder drives the company\'s expansion into multi-site commercial contracts and recurring service agreements.',
    tags: ['University of Houston', 'Marketing', 'Operations Management', 'Business Development'],
  },
];

const values = [
  { icon: '⬛', title: 'Compliance-First Execution', body: 'ADA and TAS requirements are built into every job checklist — not an afterthought. We document every measurement, every sign height, every access aisle so your property file is clean.' },
  { icon: '⬛', title: 'Veteran Discipline', body: 'Two of our principals served in the U.S. Army. That means deadlines are real, quality standards don\'t get negotiated, and we show up when we say we will — every time.' },
  { icon: '⬛', title: 'Commercial-Grade Documentation', body: 'Every compliance job ends with a closeout packet: before/after photos, measurements summary, and a dated record your property management team can file with confidence.' },
  { icon: '⬛', title: 'Portfolio Thinking', body: 'We\'re not looking for one-time jobs. We build recurring maintenance agreements and multi-site relationships with property managers who want a vendor they can count on long-term.' },
  { icon: '⬛', title: 'Community Commitment', body: 'We\'ve donated pro bono residential services to Houston homeowners who needed help. Giving back to the community we operate in isn\'t marketing — it\'s who we are.' },
  { icon: '⬛', title: 'Transparency Always', body: 'Free on-site assessments. Upfront pricing. No surprise fees. If something unexpected comes up on-site, we tell you before we touch it.' },
];

const About: React.FC = () => (
  <div className="about-page">
    {/* HERO */}
    <section className="page-hero section--dark">
      <div className="container-xl">
        <span className="section-label">Our Story</span>
        <h1 className="section-title section-title--white">Built by Veterans.<br />Driven by Standards.</h1>
        <p className="section-sub section-sub--white" style={{ marginTop: '1rem' }}>
          Pace Plan LLC is a Houston-based, SBA-certified veteran-owned commercial property services company — combining military precision, technical expertise, and a compliance-first mindset to serve commercial properties across the Greater Houston area.
        </p>
      </div>
    </section>
    <div className="red-bar" />

    {/* MISSION */}
    <section className="section">
      <div className="container-xl">
        <div className="row g-5 align-items-start">
          <div className="col-12 col-lg-7 about-mission__text">
            <span className="section-label">Our Mission</span>
            <h2 className="section-title section-title--navy">
              Helping Houston Properties Stay Compliant, Clean, and Professional.
            </h2>
            <p>
              We founded Pace Plan because we saw a gap in the market: property managers needed a vendor who understood ADA and TAS compliance details — not just "a striping guy." One who would show up at night to minimize tenant disruption, provide documentation that holds up under inspection, and build a long-term relationship rather than disappear after the first invoice.
            </p>
            <p>
              That's the company we set out to build. Every system, every checklist, every job site protocol reflects that mission.
            </p>
            <p>
              Our focus is commercial — strip centers, office parks, medical campuses, industrial sites, multi-family common areas, retail plazas — with the scale and responsiveness that portfolio property managers need from a trusted vendor.
            </p>
          </div>
          <div className="col-12 col-lg-5">
            <div className="about-mission__certs d-flex flex-column gap-3">
              <div className="cert-card">
                <div className="cert-card__icon">★</div>
                <h3>Service-Disabled Veteran-Owned Small Business</h3>
                <p>SBA Certified SDVOSB</p>
                <span className="cert-card__badge">U.S. Small Business Administration</span>
              </div>
              <div className="cert-card">
                <div className="cert-card__icon">★</div>
                <h3>Veteran-Owned Small Business</h3>
                <p>SBA Certified VOSB</p>
                <span className="cert-card__badge">U.S. Small Business Administration</span>
              </div>
              <div className="cert-card cert-card--stat">
                <div className="cert-stat-num">10+</div>
                <p>Combined years of U.S. Army service between our veteran principals</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* TEAM */}
    <section className="section section--gray">
      <div className="container-xl">
        <span className="section-label">Leadership</span>
        <h2 className="section-title section-title--navy" style={{ marginBottom: '0.5rem' }}>Meet the Team</h2>
        <p className="section-sub" style={{ marginBottom: '3rem' }}>
          A lean, experienced leadership team with deep roots in military service, technology, and business development.
        </p>
        <div className="d-flex flex-column gap-4 mb-4">
          {team.map((m, i) => (
            <div className="team-card d-flex align-items-start gap-4 p-4 bg-white border rounded-2" key={i}>
              <div className="team-card__avatar flex-shrink-0" style={{ background: m.color }}>
                {m.initials}
              </div>
              <div className="team-card__info">
                <h3>{m.name}</h3>
                <span className="team-card__title">{m.title}</span>
                <p>{m.bio}</p>
                <div className="d-flex flex-wrap gap-2">
                  {m.tags.map((t, j) => (
                    <span className="team-tag" key={j}>{t}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTO note */}
        <div className="team-note d-flex align-items-start gap-3 p-4">
          <div className="team-note__icon flex-shrink-0">
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
          </div>
          <div>
            <strong>Technology & Systems</strong>
            <p>Our technology infrastructure, digital operations, and systems architecture are managed by our founding CTO — a WGU Computer Science graduate with U.S. Army service background — ensuring that client-facing tools, job documentation, and operational software reflect the same precision standard we apply on every job site.</p>
          </div>
        </div>
      </div>
    </section>

    {/* VALUES */}
    <section className="section section--dark">
      <div className="container-xl">
        <span className="section-label">How We Operate</span>
        <h2 className="section-title section-title--white" style={{ marginBottom: '0.5rem' }}>Our Values in Practice</h2>
        <p className="section-sub section-sub--white" style={{ marginBottom: '3rem' }}>Not a poster on a wall — these are the standards we hold every job to.</p>
        <div className="row g-3">
          {values.map((v, i) => (
            <div className="col-12 col-md-6 col-lg-4" key={i}>
              <div className="value-card h-100">
                <span className="value-card__num">0{i + 1}</span>
                <h4>{v.title}</h4>
                <p>{v.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ROADMAP */}
    <section className="section">
      <div className="container-xl">
        <span className="section-label">Growth Plan</span>
        <h2 className="section-title section-title--navy" style={{ marginBottom: '2.5rem' }}>Where We're Headed</h2>
        <div className="d-flex flex-column">
          {[
            { phase: 'Phase 1', label: 'Proof & Portfolio', items: ['Build before/after portfolio across commercial and residential jobs', 'Standardize ADA audit checklist and closeout packet format', 'Establish relationships with 3–5 commercial property managers'] },
            { phase: 'Phase 2', label: 'Portfolio Penetration', items: ['Sign 2–4 recurring maintenance agreements', 'Target multi-site property management companies', 'Expand into night and weekend scheduling capability'] },
            { phase: 'Phase 3', label: 'Scale', items: ['Second field crew for expanded capacity', 'Multi-site portfolio contracts as primary revenue engine', 'Expand service lines: sealcoating, fire lane markings, signage packages'] },
          ].map((r, i) => (
            <div className="d-flex flex-column flex-md-row gap-3 py-4 border-bottom" key={i}>
              <div className="roadmap-phase__label flex-shrink-0 d-flex flex-column gap-1" style={{ width: '220px' }}>
                <span className="roadmap-badge">{r.phase}</span>
                <strong>{r.label}</strong>
              </div>
              <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
                {r.items.map((item, j) => (
                  <li className="d-flex align-items-start gap-2" key={j}>
                    <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 16, height: 16, stroke: 'var(--red)', fill: 'none', strokeWidth: 2.5, flexShrink: 0, marginTop: 2 }}><polyline points="20 6 9 17 4 12"/></svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="section--dark" style={{ borderTop: '3px solid var(--red)', padding: 'clamp(3rem,6vw,5rem) 0' }}>
      <div className="container-xl">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-4">
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 'clamp(1.6rem,3vw,2.5rem)', textTransform: 'uppercase', color: 'var(--white)', marginBottom: '0.5rem' }}>
              Work With a Team That Shows Up.
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 15, fontWeight: 300, margin: 0 }}>
              Let's talk about your property or portfolio. Free assessment, no obligation.
            </p>
          </div>
          <div className="d-flex gap-3 flex-wrap">
            <Link to="/contact" className="btn btn-brand-primary">Contact Us →</Link>
            <a href="tel:3464194499" className="btn btn-brand-outline-white">(346) 419-4499</a>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default About;
